// Security Maturity Assessment — 6 domains x 2 questions, each scored 1-5.
const q = (id, domain, prompt, options) => ({ id, domain, prompt, options });
const opt = (label, value) => ({ label, value });

export const maturityDomains = [
  'Governance & Risk',
  'Identity & Access',
  'Detection & Monitoring',
  'Incident Response',
  'Data Protection & Backup',
  'Third-Party & Vendor Risk',
];

export const maturityQuestions = [
  q('gov-1', 'Governance & Risk', 'How is your information security policy maintained?', [
    opt('No formal policy exists', 1),
    opt('Policy exists but is rarely reviewed', 2),
    opt('Policy is reviewed annually', 3),
    opt('Policy is tied to a formal, maintained risk register', 4),
    opt('Risk register actively drives resourcing and board reporting', 5),
  ]),
  q('gov-2', 'Governance & Risk', 'How is security risk communicated to leadership?', [
    opt('It is not communicated', 1),
    opt('Ad hoc updates, usually after something breaks', 2),
    opt('Periodic informal updates', 3),
    opt('Regular structured reporting', 4),
    opt('Continuous metrics leadership actively uses to make decisions', 5),
  ]),
  q('iam-1', 'Identity & Access', 'How is privileged access managed?', [
    opt('Shared admin credentials', 1),
    opt('Individual accounts, no regular review', 2),
    opt('Individual accounts with periodic access reviews', 3),
    opt('Least-privilege enforced with MFA everywhere', 4),
    opt('Just-in-time privileged access with a full audit trail', 5),
  ]),
  q('iam-2', 'Identity & Access', 'How quickly is access revoked when someone leaves the organization?', [
    opt('No formal offboarding process', 1),
    opt('Revoked within a few weeks', 2),
    opt('Revoked within days', 3),
    opt('Revoked within hours via automated workflow', 4),
    opt('Immediate, automated, and audited', 5),
  ]),
  q('det-1', 'Detection & Monitoring', 'What level of visibility do you have into your environment?', [
    opt('No centralized logging', 1),
    opt('Logs collected, rarely reviewed', 2),
    opt('Logs reviewed periodically', 3),
    opt('Business-hours SIEM monitoring', 4),
    opt('24/7 SOC with detections mapped to known attack techniques', 5),
  ]),
  q('det-2', 'Detection & Monitoring', 'How do you know whether your detections actually work?', [
    opt('We do not test detections', 1),
    opt('Reviewed only after an incident', 2),
    opt('Occasional manual review', 3),
    opt('Regular tuning based on false-positive rate', 4),
    opt('Continuous validation against real attack scenarios', 5),
  ]),
  q('ir-1', 'Incident Response', 'Do you have a tested incident response plan?', [
    opt('No plan', 1),
    opt('Plan exists, never tested', 2),
    opt('Tabletop exercise completed once', 3),
    opt('Annual tabletop exercises', 4),
    opt('Regular exercises plus post-incident reviews that update the plan', 5),
  ]),
  q('ir-2', 'Incident Response', 'Realistically, how long would it take you to detect and contain a serious incident?', [
    opt('No idea', 1),
    opt('Days to weeks', 2),
    opt('About a day', 3),
    opt('A few hours', 4),
    opt('Under an hour, with defined roles and runbooks', 5),
  ]),
  q('dp-1', 'Data Protection & Backup', 'How resilient are your backups against ransomware specifically?', [
    opt('Backups sit on the same network and credentials as production', 1),
    opt('Separate backups, but untested', 2),
    opt('Restores tested occasionally', 3),
    opt('Immutable or offline copy, tested quarterly', 4),
    opt('Full 3-2-1-1-0 strategy with defined RTO/RPO', 5),
  ]),
  q('dp-2', 'Data Protection & Backup', 'Do you know where your sensitive data actually lives?', [
    opt('No data mapping has been done', 1),
    opt('Partial understanding', 2),
    opt('Mapped for compliance purposes only', 3),
    opt('Classified with access controlled accordingly', 4),
    opt('Continuous discovery and classification tied to access policy', 5),
  ]),
  q('vendor-1', 'Third-Party & Vendor Risk', 'How do you assess a vendor\'s security before onboarding them?', [
    opt('No assessment', 1),
    opt('We ask for a compliance certificate, nothing more', 2),
    opt('Basic security questionnaire', 3),
    opt('Formal risk assessment scaled to data sensitivity', 4),
    opt('Continuous vendor risk monitoring with contractual requirements', 5),
  ]),
  q('vendor-2', 'Third-Party & Vendor Risk', 'If a key vendor were breached tomorrow, would you know your exposure?', [
    opt('No idea', 1),
    opt('We would have to ask them', 2),
    opt('We know which vendors touch sensitive data', 3),
    opt('We have a vendor inventory with defined blast radius per vendor', 4),
    opt('We actively monitor vendor risk with a response plan ready', 5),
  ]),
];

export const maturityMeta = {
  eyebrow: 'Free Tool',
  heroTitle: 'Security Maturity Assessment',
  heroDesc: 'Twelve questions across six domains — governance, identity, detection, response, data protection, and vendor risk. About five minutes, and you get a real breakdown of where your program actually stands, not just a single number.',
  resultsIntro: 'Your Maturity Breakdown',
};

const tierFor = (avg) => {
  if (avg < 2) return { tier: 'Ad Hoc', desc: 'Security happens reactively, when something forces it to. There is no shame in this — most organizations start here — but it also means you are likely one incident away from finding out where the gaps are the hard way.' };
  if (avg < 3) return { tier: 'Developing', desc: 'Some controls exist, but they are inconsistent and rarely tested end-to-end. This is the stage where formalizing a program — rather than adding more point solutions — has the biggest impact.' };
  if (avg < 4) return { tier: 'Defined', desc: 'You have documented processes and real controls in most areas. The next gain comes from testing those controls under realistic conditions rather than assuming they hold up.' };
  return { tier: 'Managed & Optimized', desc: 'Your program is mature and actively measured. At this level, the highest-value work is continuous validation — red team exercises, detection engineering, and keeping pace with how your environment changes.' };
};

export const computeMaturityResult = (answers, questions) => {
  const byDomain = {};
  questions.forEach((ques) => {
    const val = answers[ques.id] ?? 1;
    if (!byDomain[ques.domain]) byDomain[ques.domain] = [];
    byDomain[ques.domain].push(val);
  });

  const domainScores = Object.entries(byDomain).map(([domain, vals]) => ({
    domain,
    avg: vals.reduce((a, b) => a + b, 0) / vals.length,
  }));

  const overallAvg = domainScores.reduce((sum, d) => sum + d.avg, 0) / domainScores.length;
  const { tier, desc } = tierFor(overallAvg);
  const weakest = [...domainScores].sort((a, b) => a.avg - b.avg)[0];

  return {
    overallAvg,
    overallPct: Math.round((overallAvg / 5) * 100),
    tier,
    tierDesc: desc,
    domainScores,
    weakestDomain: weakest.domain,
    ctaHref: '/services/cyber-security-strategic-consulting',
    ctaLabel: 'Get a Prioritized Roadmap',
  };
};
